import {computed, ref, watch, type ComputedRef, type Ref} from 'vue'

/** THE APPEARANCE SYSTEM — two independent axes. */

export type ThemeMode = 'LIGHT' | 'DARK' | 'SYSTEM'

const MODE_KEY = 'ms-theme'
const CONTRAST_KEY = 'ms-contrast'

const MODES: readonly ThemeMode[] = ['LIGHT', 'SYSTEM', 'DARK']

const read = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

const write = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    /* Nothing to do — the choice still holds for this session. */
  }
}

const storedMode = ((): ThemeMode | null => {
  const raw = read(MODE_KEY)
  return MODES.includes(raw as ThemeMode) ? (raw as ThemeMode) : null
})()

let ownsChoice = storedMode !== null

const mode = ref<ThemeMode>(storedMode ?? 'LIGHT')
const contrast = ref<boolean>(read(CONTRAST_KEY) === '1')

const darkQuery =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null

const systemDark = ref<boolean>(darkQuery?.matches ?? false)
darkQuery?.addEventListener('change', (e: MediaQueryListEvent) => {
  systemDark.value = e.matches
})

const isDark = computed<boolean>(
  () => mode.value === 'DARK' || (mode.value === 'SYSTEM' && systemDark.value),
)

/** What the server is told: SYSTEM collapses to the theme actually on screen. */
const resolvedTheme = computed<'LIGHT' | 'DARK'>(() => (isDark.value ? 'DARK' : 'LIGHT'))

const apply = (): void => {
  const body = document.body
  if (!body) return
  body.classList.toggle('dark', isDark.value)
  body.classList.toggle('hc', contrast.value)
}

watch(isDark, apply, {immediate: true, flush: 'sync'})
watch(contrast, apply, {immediate: true, flush: 'sync'})

let hydrating = false
watch(
  mode,
  (m) => {
    if (hydrating) return
    ownsChoice = true
    write(MODE_KEY, m)
  },
  {flush: 'sync'},
)
watch(contrast, (c) => write(CONTRAST_KEY, c ? '1' : '0'), {flush: 'sync'})

/** What the next press of the cycle lands on. */
export const nextMode = (from: ThemeMode): ThemeMode =>
  MODES[(MODES.indexOf(from) + 1) % MODES.length] as ThemeMode

export interface ThemeApi {
  mode: Ref<ThemeMode>
  contrast: Ref<boolean>
  /** Whether the dark theme is on screen right now — SYSTEM already resolved. */
  isDark: ComputedRef<boolean>
  /** Whether SYSTEM currently means dark. Only interesting to a label. */
  systemDark: Ref<boolean>
  resolvedTheme: ComputedRef<'LIGHT' | 'DARK'>
  setMode: (next: ThemeMode) => void
  /** Light → System → Dark → Light. See MODES for why that order. */
  cycleMode: () => ThemeMode
  toggleContrast: (next?: boolean) => boolean
  /** The server's answer, used only by a device that has no local choice yet. */
  hydrateFromServer: (theme: 'LIGHT' | 'DARK' | undefined | null) => void
}

export const useTheme = (): ThemeApi => ({
  mode,
  contrast,
  isDark,
  systemDark,
  resolvedTheme,

  setMode: (next: ThemeMode): void => {
    mode.value = next
  },

  cycleMode: (): ThemeMode => {
    const next = nextMode(mode.value)
    mode.value = next
    return next
  },

  toggleContrast: (next?: boolean): boolean => {
    contrast.value = next ?? !contrast.value
    return contrast.value
  },

  hydrateFromServer: (theme): void => {
    if (ownsChoice || !theme) return
    hydrating = true
    mode.value = theme
    hydrating = false
  },
})
